/**
 * Scenario Hydration Coordinator
 * 
 * Pure orchestrator for scenario loading from server to local state.
 * Writes lifecycle status into MemoryStateService (single source of truth).
 * Has NO public read API for status — UI reads status from MemoryStateService.
 * 
 * Architecture:
 * - Coordination layer (sits between UI and Infrastructure)
 * - No HTTP calls (delegates to ServerSyncService)
 * - No IndexedDB writes (delegates to ServerSyncService/Domain)
 * - No status storage (delegates to MemoryStateService)
 * - Stateless except for in-flight request deduplication
 */

import { memoryStateService } from '../state/memory-store'
import { serverSyncService, type ServerSyncService } from '../infrastructure/server-sync-service'
import { localDB } from '../../db/client'
import { logger } from '../utils/logger'

export type HydrationPriority = 'high' | 'background'

export class ScenarioHydrationCoordinator {
  private readonly serverSyncService: ServerSyncService
  private readonly memoryStateService: typeof memoryStateService
  
  private inFlightRequests: Map<string, Promise<void>> = new Map()

  constructor(deps?: {
    serverSyncService?: ServerSyncService
    memoryStateService?: typeof memoryStateService
  }) {
    // Use provided dependencies or fall back to singletons
    this.serverSyncService = deps?.serverSyncService ?? serverSyncService
    this.memoryStateService = deps?.memoryStateService ?? memoryStateService
  }

  /**
   * Ensure a scenario is loaded and ready for use
   * Deduplicates concurrent requests for the same scenario
   * 
   * Writes status transitions into MemoryStateService:
   *   not-loaded → loading → loaded (or error/access-denied/deleted)
   * 
   * @param scenarioId - Scenario to load
   * @param priority - 'high' for user viewing (10s timeout), 'background' for prefetch (30s timeout)
   */
  async ensureScenarioReady(
    scenarioId: string, 
    priority: HydrationPriority = 'high'
  ): Promise<void> {
    logger.debug(`[HydrationCoordinator] ensureScenarioReady(${scenarioId}, ${priority})`)
    
    // If already loaded in memory, return immediately
    if (this.memoryStateService.isScenarioLoaded(scenarioId)) {
      logger.debug(`[HydrationCoordinator] Scenario ${scenarioId} already loaded in memory`)
      return
    }
    
    // If already hydrating, wait for existing request (deduplication)
    if (this.inFlightRequests.has(scenarioId)) {
      logger.debug(`[HydrationCoordinator] Scenario ${scenarioId} already hydrating, waiting...`)
      return this.inFlightRequests.get(scenarioId)!
    }
    
    // Start new hydration — write status into memory state
    logger.info(`[HydrationCoordinator] Starting hydration for ${scenarioId}`)
    this.memoryStateService.setScenarioLoadStatus(scenarioId, 'loading')
    
    const promise = this.doHydrate(scenarioId, priority)
    this.inFlightRequests.set(scenarioId, promise)
    
    try {
      await promise
      logger.info(`[HydrationCoordinator] Successfully hydrated ${scenarioId}`)
      // loadScenario() already sets status to 'loaded' internally
    } catch (err: any) {
      logger.error(`[HydrationCoordinator] Failed to hydrate ${scenarioId}:`, err)
      
      // Map errors to status — write into memory state
      if (err.status === 403) {
        this.memoryStateService.setScenarioLoadStatus(scenarioId, 'access-denied')
      } else if (err.status === 404) {
        this.memoryStateService.setScenarioLoadStatus(scenarioId, 'deleted')
      } else {
        this.memoryStateService.setScenarioLoadStatus(scenarioId, 'error')
      }
      
      throw err // Let caller handle if needed
    } finally {
      this.inFlightRequests.delete(scenarioId)
    }
  }

  /**
   * Internal: Perform actual hydration logic
   */
  private async doHydrate(scenarioId: string, priority: HydrationPriority): Promise<void> {
    // Step 1: Check if already in IndexedDB with valid data
    try {
      const scenario = await localDB.getScenario(scenarioId)
      
      if (scenario && scenario.sequenceNumber >= 0) {
        // Already hydrated, just load to memory
        logger.debug(`[HydrationCoordinator] Scenario ${scenarioId} found in IndexedDB (seq=${scenario.sequenceNumber})`)
        
        // Ensure it's loaded in memory (loadScenario sets status to 'loaded')
        if (!this.memoryStateService.isScenarioLoaded(scenarioId)) {
          await this.memoryStateService.loadScenario(scenarioId)
        }
        
        return // Success!
      }
    } catch (err) {
      // Scenario not in IndexedDB or error reading - continue to server fetch
      logger.debug(`[HydrationCoordinator] Scenario ${scenarioId} not in IndexedDB, fetching from server`)
    }
    
    // Step 2: Fetch from server (validation happens here - 404/403 will throw)
    // This delegates to infrastructure which handles SignalR waiting, HTTP calls, etc.
    await this.serverSyncService.hydrateScenario(scenarioId, priority)
    
    // Step 3: Now it MUST exist in IndexedDB (or we would have thrown above)
    // Load to memory for immediate use — loadScenario sets status to 'loaded'
    await this.memoryStateService.loadScenario(scenarioId)
    
    logger.info(`[HydrationCoordinator] Scenario ${scenarioId} hydrated and loaded to memory`)
  }

  /**
   * Cleanup - called on app shutdown or user logout
   */
  cleanup(): void {
    this.inFlightRequests.clear()
    logger.debug('[HydrationCoordinator] Cleaned up')
  }
}

export const scenarioHydrationCoordinator = new ScenarioHydrationCoordinator()
